import { Injectable } from '@angular/core';
import { Observable, BehaviorSubject, Subscription } from 'rxjs';
import { Firestore, doc, docData, updateDoc, collection, query, where, collectionData, deleteField } from '@angular/fire/firestore';
import { Storage, ref, getDownloadURL, uploadBytesResumable } from '@angular/fire/storage';
import { Preferences } from '../_models/preferences';
import { User } from '../_models/user';
import { isEqual } from 'lodash-es';

const defaultLogo = './assets/images/College-Board-Logo_White.png';
const defaultImage = './assets/images/icons8-upload.svg';

function storageErrorCode(error: unknown): string | undefined {
  if (error && typeof error === 'object' && 'code' in error && typeof error.code === 'string') {
    return error.code;
  }
  return undefined;
}

@Injectable()
export class UnivPrefsService {
  draftPrefs = new BehaviorSubject<Preferences | null>(null);
  publishedPrefs = new BehaviorSubject<Preferences | null>(null);
  draftSubscription: Subscription | null = null;
  publishSubscription: Subscription | null = null;
  draftChanges = new BehaviorSubject(false);
  // URL (location) of each image (where present)
  logoURL = new BehaviorSubject(defaultLogo);
  headerBgImageURL = new BehaviorSubject(defaultImage);
  pageBgImageURL = new BehaviorSubject(defaultImage);
  welcomeImageURL = new BehaviorSubject(defaultImage);

  // CSS for background-image property
  headerBgImageCss = new BehaviorSubject('');  // URL or gradient used by header style
  // not currently used in the admin app
  pageBgImageCss = new BehaviorSubject('');  //  URL or gradient used by page background style

  loadedSchoolId = '';

  activeUsers: Observable<User[]> | null = null;

  loadingLogo = './assets/images/loader.gif';

  constructor(
    private firestore: Firestore,
    private storage: Storage,
  ) { }

  loadPreferences(schoolId: string) {
    schoolId = (schoolId === '') ? 'faber' : schoolId;
    if (schoolId !== this.loadedSchoolId) {
      this.logoURL.next('');

      if (this.draftSubscription) { this.draftSubscription.unsubscribe(); }
      const draftDocRef = doc(this.firestore, 'preferences_draft', schoolId);
      this.draftSubscription = docData(draftDocRef).subscribe(prefs => {
        this.loadedSchoolId = schoolId;
        if (prefs) {
          this.loadImages(prefs as Preferences);
          // this.calcConfigSerivce.useRounding = prefs.useRounding || false;
          // this.calcConfigSerivce.efcRounding = prefs.efcRounding || false;
          // this.calcConfigSerivce.roundingValue = prefs.roundingValue || 50;
          this.draftPrefs.next(prefs as Preferences);
          this.checkPreferences();
        }
      });

      if (this.publishSubscription) { this.publishSubscription.unsubscribe(); }
      const publishedDocRef = doc(this.firestore, 'preferences', schoolId);
      this.publishSubscription = docData(publishedDocRef).subscribe(prefs => {
        // this.loadedSchoolId = schoolId;
        if (prefs) {
          this.publishedPrefs.next(prefs as Preferences);
          this.checkPreferences();
        } else {
          this.draftChanges.next(true);
        }
      });
    }

    const activeUsersCol = collection(this.firestore, 'admin_users');
    const q = query(activeUsersCol, where('schools', 'array-contains', schoolId), where('admin', '==', false));
    this.activeUsers = collectionData(q) as Observable<User[]>;
  }

  checkPreferences() {
    const draftPrefs = this.draftPrefs.value;
    const pubPrefs = this.publishedPrefs.value;
    if (pubPrefs && draftPrefs) {
      this.draftChanges.next(!isEqual(draftPrefs, pubPrefs));
    }
  }

  savePreferences(prefs: Partial<Preferences>, draft: boolean): Promise<void> {
    const schoolId = this.loadedSchoolId;
    const prefsRefPath = draft ? 'preferences_draft' : 'preferences';
    const docRef = doc(this.firestore, prefsRefPath, schoolId);
    return updateDoc(docRef, prefs);
  }

  unload() {
    if (this.draftSubscription) { this.draftSubscription.unsubscribe(); }
    if (this.publishSubscription) { this.publishSubscription.unsubscribe(); }
    this.draftPrefs.next(null);
    this.publishedPrefs.next(null);
    this.loadedSchoolId = '';
  }

  private async loadImageFromStorage(
    imagePath: string,
    defaultImg: string,
    imageSubject: BehaviorSubject<string>,
    cssSubject?: BehaviorSubject<string>,
    cssType?: string
  ) {
    if (!imagePath) {
      imageSubject.next(defaultImg);
      return;
    }

    try {
      const storageRef = ref(this.storage, imagePath);
      const url = await getDownloadURL(storageRef);
      imageSubject.next(url);
      if (cssSubject && (cssType === 'hImage' || cssType === 'bgImage')) {
        cssSubject.next('url(' + url + ')');
      }
    } catch (error) {
      if (storageErrorCode(error) === 'storage/object-not-found') {
        console.warn('Missing image: ' + imagePath);
      } else {
        console.error('Error getting download URL:', error);
      }
      imageSubject.next(defaultImg);
    }
  }

  loadImages(prefs: Preferences) {
    this.loadImageFromStorage(prefs.logoPath, defaultLogo, this.logoURL);
    this.loadImageFromStorage(prefs.headerBgImagePath, defaultImage, this.headerBgImageURL, this.headerBgImageCss, prefs.headerType);
    this.loadImageFromStorage(prefs.pageBgImagePath, defaultImage, this.pageBgImageURL, this.pageBgImageCss, prefs.backgroundType);
    this.loadImageFromStorage(prefs.welcomeImagePath, defaultImage, this.welcomeImageURL);

    switch (prefs.headerType) {
      case 'hSolid':
        this.headerBgImageCss.next('');
        break;
      case 'hGradient':
        this.headerBgImageCss.next(prefs.headerGradient);
        break;
    }

    // Not currently used in admin app
    switch (prefs.backgroundType) {
      case 'bgSolid':
        this.pageBgImageCss.next('');
        break;
      case 'bgGradient':
        this.pageBgImageCss.next(prefs.backgroundGradient);
        break;
    }
  }

  setWelcomeImageClass(schoolId: string, url: string) {
    const objUpdate = {
      welcomeImageClass: {
        'background-image': 'url(' + url + ')',
        'background-size': 'cover',
        'background-position': 'center',
        'margin-bottom': '20px',
        'width': '100%',
        'height': '300px'
      }
    };
    const docRef = doc(this.firestore, 'preferences_draft', schoolId);
    updateDoc(docRef, objUpdate);
  }

  uploadImageFile(schoolId: string, file: File, type: 'logo' | 'header' | 'page' | 'welcome'): Observable<{ success: boolean; message: string; url?: string; }> {
    const filePath = `${schoolId}/${file.name.replace(/[()']/g, '')}`;
    const storageRef = ref(this.storage, filePath);
    const task = uploadBytesResumable(storageRef, file);

    let objUpdate = {};
    switch (type) {
      case 'logo': objUpdate = { logoPath: filePath }; break;
      case 'header': objUpdate = { headerBgImagePath: filePath }; break;
      case 'page': objUpdate = { pageBgImagePath: filePath }; break;
      case 'welcome': objUpdate = { welcomeImagePath: filePath }; break;
    }

    return new Observable(observer => {
      const unsubscribe = task.on('state_changed',
        (snapshot) => {
          // Track upload progress if needed
          const progress = (snapshot.bytesTransferred / snapshot.totalBytes) * 100;
          console.log(`Upload progress: ${progress}%`);
        },
        (error) => {
          // Upload failed - handle different error types
          console.error('Upload failed:', error);
          let errorMessage = 'Upload failed. Please try again.';

          switch (error.code) {
            case 'storage/canceled':
              errorMessage = 'Upload was canceled.';
              break;
            case 'storage/unknown':
              errorMessage = 'An unknown error occurred during upload. Please try again.';
              break;
            case 'storage/invalid-checksum':
              errorMessage = 'File appears to be corrupted. Please try uploading again.';
              break;
            case 'storage/retry-limit-exceeded':
              errorMessage = 'Upload failed due to network issues. Please check your connection and try again.';
              break;
            case 'storage/invalid-format':
              errorMessage = 'Invalid file format. Please upload an image file (JPEG, PNG, GIF, etc.).';
              break;
            case 'storage/unauthorized':
              errorMessage = 'File size too large or invalid file type. Please check the file requirements.';
              break;
            default:
              // Check if it's a size or type validation error from security rules
              if (error.message && error.message.includes('size')) {
                errorMessage = 'File is too large. Please use a smaller image file.';
              } else if (error.message && error.message.includes('contentType')) {
                errorMessage = 'Invalid file type. Please upload an image file (JPEG, PNG, GIF, etc.).';
              }
              break;
          }

          observer.next({ success: false, message: errorMessage });
          observer.complete();
        },
        async () => {
          // Upload completed (success or failure)
          try {
            // Upload succeeded, update Firestore and get download URL
            await updateDoc(doc(this.firestore, 'preferences_draft', schoolId), objUpdate);
            const url = await getDownloadURL(task.snapshot.ref);
            // Update the appropriate image URL
            switch (type) {
              case 'logo': this.logoURL.next(url); break;
              case 'header': this.headerBgImageURL.next(url); break;
              case 'page': this.pageBgImageURL.next(url); break;
              case 'welcome':
                this.welcomeImageURL.next(url);
                this.setWelcomeImageClass(schoolId, url);
                break;
            }
            observer.next({ success: true, message: 'Upload successful!', url });
            observer.complete();
          } catch (error) {
            console.error('Error getting download URL:', error);
            observer.next({
              success: false,
              message: 'Upload completed but failed to get download URL. Please try again.'
            });
            observer.complete();
          }
        }
      );

      // Return cleanup function
      return () => {
        unsubscribe();
      };
    });
  }

  async removeImageRef(schoolId: string, type: string) {
    let obj = {};
    switch (type) {
      case 'logo':
        obj = { logoPath: deleteField() };
        this.logoURL.next(defaultLogo);
        break;
      case 'headerBgImage':
        obj = { headerBgImagePath: deleteField() };
        this.headerBgImageURL.next(defaultImage);
        break;
      case 'pageBgImage':
        obj = { pageBgImagePath: deleteField() };
        this.pageBgImageURL.next(defaultImage);
        break;
      case 'welcome':
        obj = { welcomeImagePath: deleteField(), welcomeImageClass: deleteField() };
        this.welcomeImageURL.next(defaultImage);
        break;
    }
    const docRef = doc(this.firestore, 'preferences_draft', schoolId);
    await updateDoc(docRef, obj);

  }

}